import { type ConfigUrls } from 'hb-react/frontoffice/apps/Forum/types';
import objectToQuery from 'hb-react/shared/utils/objectToQuery';

import { type SORT_BY } from '../constants';
import { SORT_BY_FOR_API } from '../constants';
import { getDiscussions, getWidgets } from '../requests';

type Filters = { fulltext: string } | {} | null;
type Sort = (typeof SORT_BY)[number];
type Tags = { [s: string]: number } | null;

interface FetchItems {
  fetchFilters: {
    filters: Filters;
    sort: Sort;
    tags: Tags;
  };
  page: number;
  signal?: AbortSignal;
}

const getSorting = (sort: Sort) => (sort ? SORT_BY_FOR_API[sort.value] : {});

const getFilters = (filters: Filters) =>
  filters ? objectToQuery(filters, { getKey: () => 'fulltext' }) : {};

const getTags = (tags: Tags) =>
  tags
    ? {
        category_ids: Object.values(tags),
      }
    : {};

export const fetchItemsParams = ({
  page,
  fetchFilters,
}: Omit<FetchItems, 'signal'>) => {
  const hasNoFilters =
    !fetchFilters.tags &&
    (!fetchFilters.filters || Object.keys(fetchFilters.filters).length === 0);

  const params = {
    page,
    ...getFilters(fetchFilters.filters),
    ...getTags(fetchFilters.tags),
    ...getSorting(fetchFilters.sort),
    ...(hasNoFilters && { pinned_on_index_page: false }),
  };

  return params;
};

export const makeFetchItems =
  ({ configUrls }: { configUrls: ConfigUrls }) =>
  ({ fetchFilters, page, signal }: FetchItems) =>
    getDiscussions({
      configUrls,
      params: fetchItemsParams({ page, fetchFilters }),
      signal,
    });

export const makeFetchPinnedItems =
  ({ configUrls }: { configUrls: ConfigUrls }) =>
  ({ page, signal }: { page: number; signal?: AbortSignal }) =>
    getDiscussions({
      configUrls,
      params: {
        page,
        pinned_on_index_page: true,
      },
      signal,
    });

export const makeFetchWidgets =
  ({ configUrls }: { configUrls: ConfigUrls }) =>
  () =>
    getWidgets({ configUrls });
